import {
  EditorConfig,
  LexicalNode,
  NodeKey,
  SerializedTextNode,
  TextNode,
} from 'lexical';
import {
  defaultTranslations,
  LocalizableMixin,
  SerializedLocalizable,
} from './LocalizableMixin.tsx';
import { Language } from '../../../../../../services/api/api-client.types.ts';
import { getIsCopying } from '../../plugins/CopyingState.ts';
import { $isProtectedParagraphNode } from './ProtectedParagraphNode.ts';
import { $isTabItemNode } from '../TabItemNode.ts';

export interface SerializedLocalizableTextNode
  extends SerializedTextNode,
    SerializedLocalizable {}

export class LocalizableTextNode extends LocalizableMixin(TextNode) {
  constructor(
    text: string,
    originalLanguage: Language | null,
    pageDesignerLanguage: Language | null,
    style?: string,
    format?: number,
    translations?: Record<string, string | null>,
    id?: string,
    languagesTranslatedByAI?: string[],
    languagesLastManuallyChanged?: string[],
    key?: NodeKey,
  ) {
    super(text, key);
    this.__format = format ?? 0;
    this.__style = style ?? '';
    this.__languagesTranslatedByAI = languagesTranslatedByAI ?? [];
    this.__languagesLastManuallyChanged = languagesLastManuallyChanged ?? [];
    this.__originalLanguage = originalLanguage;
    this.__id = id || crypto.randomUUID();
    this.__pageDesignerLanguage = pageDesignerLanguage ?? null;
    this.__translations = translations
      ? { ...translations }
      : defaultTranslations;
  }

  static getType(): string {
    return 'localizable';
  }

  static clone(node: LocalizableTextNode): LocalizableTextNode {
    const clone = new LocalizableTextNode(
      node.__text,
      node.__originalLanguage,
      node.__pageDesignerLanguage,
      node.__style,
      node.__format,
      node.__translations,
      node.__id,
      node.__languagesTranslatedByAI,
      node.__languagesLastManuallyChanged,
      node.__key,
    );

    if (getIsCopying()) {
      clone.__translations = defaultTranslations;
      clone.__languagesTranslatedByAI = [];
      clone.__languagesLastManuallyChanged = [];
    }

    return clone;
  }

  createDOM(config: EditorConfig): HTMLElement {
    return super.createDOM(config);
  }

  updateDOM(prevNode: this, dom: HTMLElement, config: EditorConfig): boolean {
    return super.updateDOM(prevNode, dom, config);
  }

  exportJSON(): SerializedLocalizableTextNode {
    return {
      ...(super.exportJSON() as SerializedTextNode),
      languagesTranslatedByAI: this.__languagesTranslatedByAI,
      languagesLastManuallyChanged: this.__languagesLastManuallyChanged,
      originalLanguage: this.__originalLanguage,
      type: 'localizable',
      id: this.__id ?? '',
      translations: this.__translations ?? defaultTranslations,
      pageDesignerLanguage: this.__pageDesignerLanguage,
    };
  }

  isInsideTab() {
    const paragraphNode = this.getParent();

    if ($isProtectedParagraphNode(paragraphNode)) {
      const tabItemNode = paragraphNode.getParent();

      if ($isTabItemNode(tabItemNode)) {
        return true;
      }
    }

    return false;
  }

  remove(): void {
    if (
      this.__originalLanguage &&
      this.__originalLanguage !== this.__pageDesignerLanguage &&
      !(
        this.__pageDesignerLanguage &&
        this.getTranslation(this.__pageDesignerLanguage) &&
        this.getTranslation(this.__originalLanguage) === null
      )
    ) {
      return;
    }

    super.remove();
  }

  canInsertTextBefore(): boolean {
    return true;
  }

  canInsertTextAfter(): boolean {
    return true;
  }

  isUnmergeable(): boolean {
    return false;
  }

  isTextEntity(): boolean {
    return false;
  }

  static importJSON(
    serializedNode: SerializedLocalizableTextNode,
  ): LocalizableTextNode {
    const {
      text,
      format,
      style,
      originalLanguage,
      id,
      translations = {},
      languagesTranslatedByAI = [],
      languagesLastManuallyChanged = [],
      pageDesignerLanguage,
    } = serializedNode;

    const capitalizeFirst = (
      lang: string | null | undefined,
    ): string | null => {
      if (!lang) return null;
      const t = lang.trim();
      if (t.length === 0) return null;
      return t.charAt(0).toUpperCase() + t.slice(1);
    };

    const normalizedOriginalLanguage = capitalizeFirst(originalLanguage);
    const normalizedPageDesignerLanguage =
      capitalizeFirst(pageDesignerLanguage);

    const normalizedTranslations: Record<string, string | null> = {};
    for (const [key, value] of Object.entries(translations)) {
      const language = capitalizeFirst(key) ?? key;
      if (!(language in normalizedTranslations)) {
        normalizedTranslations[language] = value;
      } else {
        if (normalizedTranslations[language] == null && value != null) {
          normalizedTranslations[language] = value;
        }
      }
    }

    const normalizedLanguagesTranslatedByAI = languagesTranslatedByAI
      .map(capitalizeFirst)
      .filter((l): l is string => !!l)
      .filter((v, i, a) => a.indexOf(v) === i);

    const normalizedLanguagesLastManuallyChanged = languagesLastManuallyChanged
      .map(capitalizeFirst)
      .filter((l): l is string => !!l)
      .filter((v, i, a) => a.indexOf(v) === i);

    const node = $createLocalizableTextNode(
      text,
      normalizedOriginalLanguage as Language,
      normalizedPageDesignerLanguage as Language,
    );

    node.__id = id;
    node.__style = style;
    node.__format = format;
    node.__translations = normalizedTranslations;
    node.__languagesTranslatedByAI = normalizedLanguagesTranslatedByAI;
    node.__languagesLastManuallyChanged =
      normalizedLanguagesLastManuallyChanged;
    return node;
  }
}

export function $createLocalizableTextNode(
  text: string,
  originalLanguage: Language | null,
  pageDesignerLanguage: Language | null,
  style?: string,
): LocalizableTextNode {
  return new LocalizableTextNode(
    text,
    originalLanguage,
    pageDesignerLanguage,
    style,
  );
}

export function $isLocalizableTextNode(
  node: LexicalNode | null | undefined,
): node is LocalizableTextNode {
  return node instanceof LocalizableTextNode;
}
